import { useEffect, useMemo, useState } from 'react'
import { CalendarDays, Download, FileText, ListTree, Loader2, RefreshCw } from 'lucide-react'
import api from '../../utils/api'
import PackageSearchSelect from './PackageSearchSelect'
import { exportRowsToExcel } from '../../utils/exportExcel'

function formatDateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(dateText, days) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  return formatDateText(date)
}

function getDefaultStartDate(days) {
  return addDays(formatDateText(new Date()), -(days - 1))
}

function createSummary() {
  return {
    total_registered_count: 0,
    total_used_user_count: 0,
    total_recharge_user_count: 0,
    total_recharge_total_amount: 0,
    total_first_recharge_user_count_1_3d: 0,
    total_second_recharge_user_count_1_3d: 0,
    total_first_recharge_user_count_1_7d: 0,
    total_second_recharge_user_count_1_7d: 0,
    total_first_recharge_user_count_1_15d: 0,
    total_second_recharge_user_count_1_15d: 0,
    total_first_recharge_user_count_1_30d: 0,
    total_second_recharge_user_count_1_30d: 0
  }
}

function formatRechargePair(firstValue, secondValue) {
  return `${Number(firstValue || 0)}/${Number(secondValue || 0)}`
}

function formatAmount(value) {
  const amount = Number(value || 0)
  return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function RechargeReportStatsTab() {
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(getDefaultStartDate(30))
  const [endDate, setEndDate] = useState(formatDateText(new Date()))
  const [channelSources, setChannelSources] = useState([])
  const [filters, setFilters] = useState({
    startDate: getDefaultStartDate(30),
    endDate: formatDateText(new Date()),
    channelSources: []
  })
  const [latestDate, setLatestDate] = useState('')
  const [amountCutoffDate, setAmountCutoffDate] = useState('')
  const [summary, setSummary] = useState(createSummary())
  const [list, setList] = useState([])
  const [sourceOptions, setSourceOptions] = useState([])
  const [viewMode, setViewMode] = useState('summary')

  const fetchData = async (nextFilters = filters) => {
    setLoading(true)
    try {
      const response = await api.get('/admin/recharge-report-stats', {
        params: {
          start_date: nextFilters.startDate || undefined,
          end_date: nextFilters.endDate || undefined,
          channel_sources: Array.isArray(nextFilters.channelSources) && nextFilters.channelSources.length > 0
            ? nextFilters.channelSources
            : undefined
        }
      })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setLatestDate(data.latest_date || '')
        setAmountCutoffDate(data.amount_cutoff_date || '')
        setSummary({ ...createSummary(), ...(data.summary || {}) })
        setList(Array.isArray(data.list) ? data.list : [])
        setSourceOptions(Array.isArray(data.source_options) ? data.source_options : [])
      } else {
        setAmountCutoffDate('')
        setSummary(createSummary())
        setList([])
      }
    } catch (error) {
      console.error('获取充值报表失败:', error)
      setAmountCutoffDate('')
      setSummary(createSummary())
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(filters)
  }, [])

  const handleSubmit = (event) => {
    event.preventDefault()
    const nextFilters = {
      startDate: startDate || getDefaultStartDate(30),
      endDate: endDate || formatDateText(new Date()),
      channelSources
    }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleReset = () => {
    const nextFilters = {
      startDate: getDefaultStartDate(30),
      endDate: formatDateText(new Date()),
      channelSources: []
    }
    setStartDate(nextFilters.startDate)
    setEndDate(nextFilters.endDate)
    setChannelSources([])
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const tableRows = useMemo(() => (
    [...list].sort((a, b) => {
      const dateCompare = String(b.stat_date || '').localeCompare(String(a.stat_date || ''))
      if (dateCompare !== 0) return dateCompare
      const registeredCompare = Number(b.registered_count || 0) - Number(a.registered_count || 0)
      if (registeredCompare !== 0) return registeredCompare
      return String(a.channel_source || '').localeCompare(String(b.channel_source || ''))
    })
  ), [list])

  const detailRows = useMemo(() => {
    const groupedMap = new Map()

    for (const item of tableRows) {
      const day = String(item.stat_date || '').trim()
      if (!day) continue
      if (!groupedMap.has(day)) {
        groupedMap.set(day, [])
      }
      groupedMap.get(day).push(item)
    }

    return Array.from(groupedMap.entries()).flatMap(([day, items]) => {
      const sortedItems = [...items].sort((a, b) => {
        const registeredCompare = Number(b.registered_count || 0) - Number(a.registered_count || 0)
        if (registeredCompare !== 0) return registeredCompare
        return String(a.channel_source || '').localeCompare(String(b.channel_source || ''))
      })

      const totalRow = sortedItems.reduce((acc, item) => {
        acc.registered_count += Number(item.registered_count || 0)
        acc.used_user_count += Number(item.used_user_count || 0)
        acc.recharge_user_count += Number(item.recharge_user_count || 0)
        acc.recharge_total_amount += Number(item.recharge_total_amount || 0)
        acc.first_recharge_user_count_1_3d += Number(item.first_recharge_user_count_1_3d || 0)
        acc.second_recharge_user_count_1_3d += Number(item.second_recharge_user_count_1_3d || 0)
        acc.first_recharge_user_count_1_7d += Number(item.first_recharge_user_count_1_7d || 0)
        acc.second_recharge_user_count_1_7d += Number(item.second_recharge_user_count_1_7d || 0)
        acc.first_recharge_user_count_1_15d += Number(item.first_recharge_user_count_1_15d || 0)
        acc.second_recharge_user_count_1_15d += Number(item.second_recharge_user_count_1_15d || 0)
        acc.first_recharge_user_count_1_30d += Number(item.first_recharge_user_count_1_30d || 0)
        acc.second_recharge_user_count_1_30d += Number(item.second_recharge_user_count_1_30d || 0)
        return acc
      }, {
        stat_date: day,
        channel_source: '当日合计',
        registered_count: 0,
        used_user_count: 0,
        recharge_user_count: 0,
        recharge_total_amount: 0,
        first_recharge_user_count_1_3d: 0,
        second_recharge_user_count_1_3d: 0,
        first_recharge_user_count_1_7d: 0,
        second_recharge_user_count_1_7d: 0,
        first_recharge_user_count_1_15d: 0,
        second_recharge_user_count_1_15d: 0,
        first_recharge_user_count_1_30d: 0,
        second_recharge_user_count_1_30d: 0,
        rowType: 'total'
      })

      return [
        ...sortedItems.map((item) => ({
          ...item,
          rowType: 'data'
        })),
        totalRow
      ]
    })
  }, [tableRows])

  const summaryRows = useMemo(() => {
    const sourceMap = new Map()

    for (const item of tableRows) {
      const sourceLabel = String(item.channel_source || '').trim()
      if (!sourceLabel) continue

      if (!sourceMap.has(sourceLabel)) {
        sourceMap.set(sourceLabel, {
          channel_source: sourceLabel,
          registered_count: 0,
          used_user_count: 0,
          recharge_user_count: 0,
          recharge_total_amount: 0,
          first_recharge_user_count_1_3d: 0,
          second_recharge_user_count_1_3d: 0,
          first_recharge_user_count_1_7d: 0,
          second_recharge_user_count_1_7d: 0,
          first_recharge_user_count_1_15d: 0,
          second_recharge_user_count_1_15d: 0,
          first_recharge_user_count_1_30d: 0,
          second_recharge_user_count_1_30d: 0
        })
      }

      const current = sourceMap.get(sourceLabel)
      current.registered_count += Number(item.registered_count || 0)
      current.used_user_count += Number(item.used_user_count || 0)
      current.recharge_user_count += Number(item.recharge_user_count || 0)
      current.recharge_total_amount += Number(item.recharge_total_amount || 0)
      current.first_recharge_user_count_1_3d += Number(item.first_recharge_user_count_1_3d || 0)
      current.second_recharge_user_count_1_3d += Number(item.second_recharge_user_count_1_3d || 0)
      current.first_recharge_user_count_1_7d += Number(item.first_recharge_user_count_1_7d || 0)
      current.second_recharge_user_count_1_7d += Number(item.second_recharge_user_count_1_7d || 0)
      current.first_recharge_user_count_1_15d += Number(item.first_recharge_user_count_1_15d || 0)
      current.second_recharge_user_count_1_15d += Number(item.second_recharge_user_count_1_15d || 0)
      current.first_recharge_user_count_1_30d += Number(item.first_recharge_user_count_1_30d || 0)
      current.second_recharge_user_count_1_30d += Number(item.second_recharge_user_count_1_30d || 0)
    }

    return Array.from(sourceMap.values()).sort((a, b) => {
      const registeredCompare = Number(b.registered_count || 0) - Number(a.registered_count || 0)
      if (registeredCompare !== 0) return registeredCompare
      const usedCompare = Number(b.used_user_count || 0) - Number(a.used_user_count || 0)
      if (usedCompare !== 0) return usedCompare
      return String(a.channel_source || '').localeCompare(String(b.channel_source || ''))
    })
  }, [tableRows])

  const handleExport = () => {
    const exportRows = (viewMode === 'detail' ? detailRows : summaryRows).map((item) => ({
      stat_date: viewMode === 'detail' ? item.stat_date : '区间汇总',
      channel_source: item.channel_source || '-',
      registered_count: Number(item.registered_count || 0),
      used_user_count: Number(item.used_user_count || 0),
      recharge_user_count: Number(item.recharge_user_count || 0),
      recharge_total_amount: Number(Number(item.recharge_total_amount || 0).toFixed(2)),
      recharge_pair_1_3d: formatRechargePair(item.first_recharge_user_count_1_3d, item.second_recharge_user_count_1_3d),
      recharge_pair_1_7d: formatRechargePair(item.first_recharge_user_count_1_7d, item.second_recharge_user_count_1_7d),
      recharge_pair_1_15d: formatRechargePair(item.first_recharge_user_count_1_15d, item.second_recharge_user_count_1_15d),
      recharge_pair_1_30d: formatRechargePair(item.first_recharge_user_count_1_30d, item.second_recharge_user_count_1_30d)
    }))

    if (exportRows.length === 0) {
      return
    }

    exportRowsToExcel({
      fileName: `充值报表_${filters.startDate || ''}_${filters.endDate || latestDate || 'latest'}`,
      sheetName: '充值报表',
      columns: [
        { header: viewMode === 'detail' ? '日期' : '汇总维度', key: 'stat_date', width: 14 },
        { header: '渠道来源', key: 'channel_source', width: 22 },
        { header: '注册人数', key: 'registered_count', width: 12 },
        { header: '使用人数', key: 'used_user_count', width: 12 },
        { header: '注册日首充人数', key: 'recharge_user_count', width: 14 },
        { header: '充值总金额', key: 'recharge_total_amount', width: 14 },
        { header: '3天内首充/二充', key: 'recharge_pair_1_3d', width: 16 },
        { header: '7天内首充/二充', key: 'recharge_pair_1_7d', width: 16 },
        { header: '15天内首充/二充', key: 'recharge_pair_1_15d', width: 16 },
        { header: '30天内首充/二充', key: 'recharge_pair_1_30d', width: 16 }
      ],
      rows: exportRows
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">充值报表</h2>
          <p className="mt-1 text-sm text-gray-500">按日期和渠道来源统计注册、当日使用、注册日首充以及首充/二充 3/7/15/30 天窗口人数；充值总金额口径为该注册批次用户从注册日起累计到昨日的成功充值金额</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <PackageSearchSelect
            value={channelSources}
            onChange={setChannelSources}
            options={sourceOptions}
            multiple
            placeholder="全部渠道来源"
            searchPlaceholder="搜索渠道来源"
            className="min-w-[220px]"
          />

          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(filters)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || tableRows.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="h-4 w-4" />
            导出 Excel
          </button>
        </form>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">注册人数</div>
          <div className="mt-2 text-2xl font-bold text-blue-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_registered_count || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">使用人数</div>
          <div className="mt-2 text-2xl font-bold text-emerald-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_used_user_count || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">注册日首充人数</div>
          <div className="mt-2 text-2xl font-bold text-orange-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_recharge_user_count || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">充值总金额</div>
          <div className="mt-2 text-2xl font-bold text-fuchsia-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : formatAmount(summary.total_recharge_total_amount)}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">3天内首充 / 二充</div>
          <div className="mt-2 text-2xl font-bold text-violet-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : formatRechargePair(summary.total_first_recharge_user_count_1_3d, summary.total_second_recharge_user_count_1_3d)}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">7天内首充 / 二充</div>
          <div className="mt-2 text-2xl font-bold text-cyan-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : formatRechargePair(summary.total_first_recharge_user_count_1_7d, summary.total_second_recharge_user_count_1_7d)}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">15天内首充 / 二充</div>
          <div className="mt-2 text-2xl font-bold text-amber-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : formatRechargePair(summary.total_first_recharge_user_count_1_15d, summary.total_second_recharge_user_count_1_15d)}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">30天内首充 / 二充</div>
          <div className="mt-2 text-2xl font-bold text-rose-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : formatRechargePair(summary.total_first_recharge_user_count_1_30d, summary.total_second_recharge_user_count_1_30d)}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>当前区间：{filters.startDate} 至 {filters.endDate}，最新统计日期：{latestDate || '--'}，充值总金额截止：{amountCutoffDate || '--'}</div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => setViewMode('detail')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'detail'
                  ? 'bg-white font-medium text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ListTree className="h-4 w-4" />
              展开明细
            </button>
            <button
              type="button"
              onClick={() => setViewMode('summary')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
                viewMode === 'summary'
                  ? 'bg-white font-medium text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="h-4 w-4" />
              汇总列表
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {viewMode === 'detail' ? '日期' : '汇总维度'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">渠道来源</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">注册人数</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">使用人数</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">注册日首充人数</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">充值总金额</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">3天内首充/二充</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">7天内首充/二充</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">15天内首充/二充</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">30天内首充/二充</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : viewMode === 'detail' ? (
                detailRows.length > 0 ? (
                  detailRows.map((item, index) => (
                    <tr
                      key={`${item.stat_date}_${item.channel_source}_${item.rowType}_${index}`}
                      className={item.rowType === 'total' ? 'border-t-4 border-gray-300 bg-blue-50' : 'hover:bg-gray-50'}
                    >
                      <td className={`px-4 py-4 text-sm ${item.rowType === 'total' ? 'font-semibold text-gray-900' : 'text-gray-900'}`}>
                        {item.stat_date}
                      </td>
                      <td className={`px-4 py-4 text-sm ${item.rowType === 'total' ? 'font-semibold text-blue-700' : 'text-gray-900'}`}>
                        {item.channel_source || '-'}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-blue-700' : 'text-blue-600'}`}>
                        {item.registered_count || 0}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm ${item.rowType === 'total' ? 'font-bold text-emerald-700' : 'text-gray-900'}`}>
                        {item.used_user_count || 0}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-orange-700' : 'text-orange-600'}`}>
                        {item.recharge_user_count || 0}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm font-medium ${item.rowType === 'total' ? 'font-bold text-fuchsia-700' : 'text-fuchsia-600'}`}>
                        {formatAmount(item.recharge_total_amount)}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm ${item.rowType === 'total' ? 'font-bold text-violet-700' : 'text-gray-900'}`}>
                        {formatRechargePair(item.first_recharge_user_count_1_3d, item.second_recharge_user_count_1_3d)}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm ${item.rowType === 'total' ? 'font-bold text-cyan-700' : 'text-gray-900'}`}>
                        {formatRechargePair(item.first_recharge_user_count_1_7d, item.second_recharge_user_count_1_7d)}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm ${item.rowType === 'total' ? 'font-bold text-amber-700' : 'text-gray-900'}`}>
                        {formatRechargePair(item.first_recharge_user_count_1_15d, item.second_recharge_user_count_1_15d)}
                      </td>
                      <td className={`px-4 py-4 text-right text-sm ${item.rowType === 'total' ? 'font-bold text-rose-700' : 'text-gray-900'}`}>
                        {formatRechargePair(item.first_recharge_user_count_1_30d, item.second_recharge_user_count_1_30d)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="px-6 py-10 text-center text-sm text-gray-500">
                      当前区间暂无数据
                    </td>
                  </tr>
                )
              ) : summaryRows.length > 0 ? (
                summaryRows.map((item, index) => (
                  <tr key={`${item.channel_source}_${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-500">区间汇总</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.channel_source || '-'}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-blue-600">{item.registered_count || 0}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{item.used_user_count || 0}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-orange-600">{item.recharge_user_count || 0}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-fuchsia-600">{formatAmount(item.recharge_total_amount)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_3d, item.second_recharge_user_count_1_3d)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_7d, item.second_recharge_user_count_1_7d)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_15d, item.second_recharge_user_count_1_15d)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatRechargePair(item.first_recharge_user_count_1_30d, item.second_recharge_user_count_1_30d)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-6 py-10 text-center text-sm text-gray-500">
                    当前区间暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
